import { lazy, Suspense, useEffect, useState } from 'react'
import DesignSystemPage from './design-system/DesignSystemPage'

// Each hostname/hash surface is an independent operating context. Loading all five at
// startup made a storefront buyer download Back Ops, event-day and design-system code.
// Keep the router itself tiny and let Vite emit a cacheable chunk per surface.
const RealApp=lazy(()=>import('./real/RealApp'))
const OpsApp=lazy(()=>import('./ops/OpsApp'))
const RootSurface=lazy(()=>import('./RootSurface'))
const CompanionApp=lazy(()=>import('./companion/CompanionApp'))

// `/t/<credential>` is the link printed on a physical ticket. It belongs to the application
// surface, not the storefront, and is kept short because it has to fit on a card.
//
// S11-E: `/event--<slug>` is a published event's own public page. "Application surface" here
// means the bundle that owns the landing renderer and the checkout it hands a buyer to - not
// "signed in". Sending it to the storefront would have needed a second renderer of the same
// page, which is the one thing the reusable skeleton exists to prevent.
// AURION Ops is a back-office deployment: it has no storefront, no buyer surface and no
// public landing. That is enforced at build time rather than by a route guard, because a
// guard can be reached around - a hostname alias, a stray hash, a proxy that strips a path
// prefix - and each of those had to render *something*. With the surface pinned there is no
// input that produces a marketing page on an operations host: the sign-in IS the landing page.
const pinnedSurface=import.meta.env.VITE_SURFACE as string|undefined

// Hosts that are operations consoles even when the bundle was not built pinned.
const OPS_HOSTS=new Set(['ops.etixhub.com','ops.aurionglobal.my'])

function surface() {
  const route=window.location.hash.replace(/^#/, '')
  if(pinnedSurface)return pinnedSurface
  if(OPS_HOSTS.has(window.location.hostname.toLowerCase()))return 'ops'
  return route.startsWith('/design-system')?'design-system':route.startsWith('/ops')?'ops':route.startsWith('/companion')?'companion':route.startsWith('/app')||route.startsWith('/t/')||route.startsWith('/event--')?'app':'storefront'
}

export default function AppRouter() {
  const [activeSurface, setActiveSurface] = useState(surface)
  useEffect(() => {
    const sync = () => setActiveSurface(surface())
    window.addEventListener('hashchange', sync)
    return () => window.removeEventListener('hashchange', sync)
  }, [])
  const page=activeSurface==='design-system'?<DesignSystemPage />:activeSurface==='ops'?<OpsApp />:activeSurface==='companion'?<CompanionApp />:activeSurface==='app'?<RealApp />:<RootSurface />
  return <Suspense fallback={<main className="real-loading" role="status">{pinnedSurface==='ops'?'Loading AURION Ops…':'Loading EtixHub…'}</main>}>{page}</Suspense>
}
